import './RouteListItem.scss'

import { HDSIcon } from '@here/hds-react-components'
import ListItem from 'components/App/components/ListItem'
import BikeElevationInformation from 'components/Rdc/BikeElevationInformation'
import { getDelayColorClass } from 'components/Rdc/RdcSection/DelayTime/DelayTime'
import RoutePathInfo from 'components/Rdc/RoutePathInfo'
import { getTransportModeTranslation } from 'components/Rdc/RoutePathInfo/RoutePathInfo'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import { StateRoute } from 'data/model/routePlanner'
import { useAppSelector } from 'data/store/hooks'
import { format, secondsToHours, secondsToMinutes } from 'date-fns'
import { i18n } from 'lib/i18n'
import { fromStateRouteSectionToRouteListItemSection } from 'lib/route-planner/formatters'
import React, { useCallback, useMemo } from 'react'
import { CountryAndUnit, formatDistance, formatLocalTimeDuration } from 'utils/formatters'

interface RouteListItemProps {
  onClick: () => void
  hightlight?: boolean
  onHover?: (id: string) => void
  route: StateRoute
  transportMode: TransportModesPanelMode
}
export interface ElevationInfo {
  elevation_down: number
  elevation_up: number
}
export enum RouteListItemTransportMode {
  Pedestrian = 'walking',
  Train = 'rail-local',
  Tram = 'tram',
  Subway = 'metro',
  Car = 'driving',
  Bus = 'bus',
  Ferry = 'ferry',
  Bicycle = 'bicycle',
  Unknown = 'unknown',
}

export interface RouteListItemSection {
  title: string
  departsIn?: number
  duration: number
  mode: RouteListItemTransportMode
  colors: RouteListItemColorOptions
  delay?: number
}

export interface RouteListItemColorOptions {
  text: string
  chip: string
  icon: string
}

export function RouteListItem({
  onClick,
  hightlight,
  onHover,
  transportMode,
  route,
}: RouteListItemProps): JSX.Element {
  const countryCode = useAppSelector((state) => state.user.countryCode)
  const unit = useAppSelector((state) => state.app.unit)
  const delayTranslation =
    route.delay !== undefined && route.delay >= 60
      ? i18n('route.time.with.traffic', [formatLocalTimeDuration(route.delay)])
      : undefined
  const delayColorClass = useMemo(
    () => getDelayColorClass(route.duration, route.delay, 'RouteListItem__delay--traffic-'),
    [route.delay, route.duration]
  )
  const formattedArrivalTime = format(new Date(route.arrival), 'HH:mm')
  const hasDelay = route.delay !== undefined && route.delay !== 0
  const formattedDuration = formatLocalTimeDuration(route.duration)
  const sections = route.sections.map((section) =>
    fromStateRouteSectionToRouteListItemSection(section)
  )

  const handleRouteListItemMouseEnter = useCallback((): void => {
    onHover && onHover(route.id)
  }, [onHover, route.id])

  return (
    <ListItem
      highlight={hightlight}
      className="RouteListItem__container"
      onClick={onClick}
      onMouseEnter={handleRouteListItemMouseEnter}
    >
      <div className="RouteListItem__title-content">
        <div className="RouteListItem__title-container">
          <section className="RouteListItem__header">
            <div className="RouteListItem__title">{formattedDuration}</div>
            {hasDelay && (
              <div className={`RouteListItem__delay ${delayColorClass}`}>{delayTranslation}</div>
            )}
          </section>
          <div className="RouteListItem__subtitle">{formattedArrivalTime}</div>
        </div>
        <div className="RouteListItem__details-container">
          <RouteListItemSubtitle
            sections={sections}
            isSingleTransportMode={sections.length === 1}
            distance={route.distance}
            elevationInfo={route.elevationInfo}
            transportMode={transportMode}
            countryAndUnit={{ countryCode, unit }}
          ></RouteListItemSubtitle>
        </div>
        <div className="RouteListItem__mode-details-container">
          <RoutePathInfo sections={sections} transportMode={transportMode} />
        </div>
      </div>
    </ListItem>
  )
}

interface ItemDetailsProps {
  sections: RouteListItemSection[]
  isSingleTransportMode: boolean
  distance?: number
  elevationInfo?: ElevationInfo
  transportMode: TransportModesPanelMode
  countryAndUnit: CountryAndUnit
}

function RouteListItemSubtitle({
  sections,
  isSingleTransportMode,
  distance,
  elevationInfo,
  transportMode,
  countryAndUnit,
}: ItemDetailsProps): JSX.Element | null {
  let details =
    distance !== undefined ? (
      <span className="ItemDetails">
        {formatDistance(distance, countryAndUnit)}
        {transportMode === TransportModesPanelMode.Bike && (
          <>
            {elevationInfo && <span className="ElevationInfo_bullet">&bull;</span>}
            {elevationInfo?.elevation_down === 0 && elevationInfo?.elevation_up === 0 ? (
              <span> {i18n('route.instruction.mostly-flat')} </span>
            ) : (
              <BikeElevationInformation
                elevationInfo={elevationInfo}
                countryAndUnit={countryAndUnit}
              />
            )}
          </>
        )}
      </span>
    ) : null

  if (!isSingleTransportMode) {
    const firstTransitSection = sections.find(
      (section) => section.mode !== RouteListItemTransportMode.Pedestrian
    )
    if (firstTransitSection !== undefined && firstTransitSection.departsIn !== undefined) {
      const departureText =
        firstTransitSection.departsIn >= 3600
          ? `${secondsToHours(firstTransitSection.departsIn)} ${i18n('time-unit.hour')}`
          : `${secondsToMinutes(firstTransitSection.departsIn)} ${i18n('time-unit.minutes')}`
      const transport = getTransportModeTranslation(firstTransitSection.mode)

      if (firstTransitSection.delay !== undefined) {
        details = (
          <span className="ItemDetails">
            <span>{i18n('route.item.live.details', [transport])}</span>
            <HDSIcon name="wifi-strong" size="16px" className="ItemDetails__wifi" />
            <span>{departureText}</span>
          </span>
        )
      } else {
        details = (
          <span className="ItemDetails">
            <span>{i18n('route.item.scheduled.details', [transport])}</span>
            <span>{departureText}</span>
          </span>
        )
      }
    }
  }

  return details
}
